import { MedicalData, MedicalQuestion, MedicalQuestions } from './health-declaration.model';
import { CheckboxData } from '../travelers-checkboxes/travelers-checkboxes.component';
import { CheckboxItem } from '@pc/common';

export class MedicalQuestionVm {
  private id: number;
  private parentId: number;
  private isDropDown: boolean;
  private isRadioButton: boolean;
  private questionText: string;
  private questionGender: number;
  private raiderId: number;
  private medicalAnswer: MedicalAnswerVm | null;
  private dropDownOptions: CheckboxItem[];
  private children: MedicalQuestionVm[] | undefined;
  private travelerId: string | undefined;
  private travelerName: string | undefined;
  private isAnswer: boolean;

  constructor(dto?: MedicalQuestion) {
    if (dto) {
      this.id = dto.App_Medical_QuestionID;
      this.parentId = dto.App_Medical_QuestionParentID;
      this.isDropDown = dto.IsDropDown;
      this.isRadioButton = dto.IsRadioButton;
      this.questionText = dto.QuestionText;
      this.questionGender = dto.QuestionGenderTypeId;
      this.raiderId = dto.RaiderID;
    }
    this.children = [];
    this.dropDownOptions = [];
    this.travelerId = undefined;
    this.travelerName = undefined;
    this.isAnswer = false;
  }

  // Getter and Setter for id
  public getId(): number {
    return this.id;
  }

  public setId(id: number): void {
    this.id = id;
  }

  // Getter and Setter for parentId
  public getParentId(): number {
    return this.parentId;
  }

  public setParentId(parentId: number): void {
    this.parentId = parentId;
  }

  // Getter and Setter for isDropDown
  public getIsDropDown(): boolean {
    return this.isDropDown;
  }

  public setIsDropDown(isDropDown: boolean): void {
    this.isDropDown = isDropDown;
  }

  // Getter and Setter for isRadioButton
  public getIsRadioButton(): boolean {
    return this.isRadioButton;
  }

  public setIsRadioButton(isRadioButton: boolean): void {
    this.isRadioButton = isRadioButton;
  }

  // Getter and Setter for questionText
  public getQuestionText(): string {
    return this.questionText;
  }

  public setQuestionText(questionText: string): void {
    this.questionText = questionText;
  }

  // Getter and Setter for questionGender
  public getQuestionGender(): number {
    return this.questionGender;
  }

  public setQuestionGender(questionGender: number): void {
    this.questionGender = questionGender;
  }

  // Getter and Setter for raiderId
  public getRaiderId(): number {
    return this.raiderId;
  }

  public setRaiderId(raiderId: number): void {
    this.raiderId = raiderId;
  }

  public getMedicalAnswer(): MedicalAnswerVm | null {
    return this.medicalAnswer;
  }

  public setMedicalAnswer(value: MedicalAnswerVm | null): void {
    this.medicalAnswer = value;
  }

  public getChildren(): MedicalQuestionVm[] | undefined {
    return this.children;
  }

  public setChildren(value: MedicalQuestionVm[] | undefined): void {
    if (value) this.children = [...value];
  }

  public getDropDownOptions(): CheckboxItem[] | undefined {
    return this.dropDownOptions;
  }

  public setDropDownOptions(value: CheckboxItem[]): void {
    this.dropDownOptions = [...value];
  }

  public getTravelerId(): string | undefined {
    return this.travelerId;
  }

  public setTravelerId(value: string | undefined): void {
    this.travelerId = value;
  }

  public getTravelerName(): string | undefined {
    return this.travelerName;
  }

  public setTravelerName(value: string): void {
    this.travelerName = value;
  }

  public getIsAnswered(): boolean {
    return this.isAnswer;
  }

  public setIsAnswered(value: boolean): void {
    this.isAnswer = value;
  }
}

export class MedicalDataVm {
  private questionnaireID: number;
  private questionnaireName: string;
  private questionnaireNameLocal: string;
  private isSpecialQuestionnaire: boolean;
  private questions: MedicalQuestionsVm;
  constructor(dto: MedicalData) {
    this.questionnaireID = dto.QuestionnaireID;
    this.questionnaireName = dto.QuestionnaireName;
    this.questionnaireNameLocal = dto.QuestionnaireNameLocal;
    this.isSpecialQuestionnaire = dto.IsSpecialQuestionnaire;
    this.questions = this.mapQuestions(dto.Questions);
  }

  // Getter and Setter for questionnaireID
  public getQuestionnaireID(): number {
    return this.questionnaireID;
  }

  public setQuestionnaireID(questionnaireID: number): void {
    this.questionnaireID = questionnaireID;
  }

  // Getter and Setter for questionnaireName
  public getQuestionnaireName(): string {
    return this.questionnaireName;
  }

  public setQuestionnaireName(questionnaireName: string): void {
    this.questionnaireName = questionnaireName;
  }

  // Getter and Setter for questionnaireNameLocal
  public getQuestionnaireNameLocal(): string {
    return this.questionnaireNameLocal;
  }

  public setQuestionnaireNameLocal(questionnaireNameLocal: string): void {
    this.questionnaireNameLocal = questionnaireNameLocal;
  }

  // Getter and Setter for isSpecialQuestionnaire
  public getIsSpecialQuestionnaire(): boolean {
    return this.isSpecialQuestionnaire;
  }

  public setIsSpecialQuestionnaire(isSpecialQuestionnaire: boolean): void {
    this.isSpecialQuestionnaire = isSpecialQuestionnaire;
  }

  // Getter and Setter for questions
  public getQuestions(): MedicalQuestionsVm {
    return this.questions;
  }

  public setQuestions(questions: MedicalQuestionsVm): void {
    this.questions = questions;
  }

  private mapQuestions(questions: MedicalQuestions): MedicalQuestionsVm {
    const quest: MedicalQuestionsVm = [];
    questions.forEach((question) => {
      const res = new MedicalQuestionVm(question);
      quest.push(res);
    });
    return quest;
  }
}

export class MedicalAnswerVm {
  private answerId: number | undefined;
  private checkboxes: CheckboxData;
  private questionId: number | undefined;

  constructor(answer: number | undefined, checkboxes: CheckboxData, questionId: number | undefined) {
    this.answerId = answer;
    this.checkboxes = checkboxes;
    this.questionId = questionId;
  }

  public getAnswer(): number | undefined {
    return this.answerId;
  }

  public setAnswer(value: number): void {
    this.answerId = value;
  }

  public getCheckboxes(): CheckboxData {
    return this.checkboxes;
  }

  public setCheckboxes(value: CheckboxData): void {
    this.checkboxes = value;
  }

  public getQuestionId(): number | undefined {
    return this.questionId;
  }

  public setQuestionId(value: number): void {
    this.questionId = value;
  }
}

export type MedicalQuestionsVm = MedicalQuestionVm[];

export interface AnalyticsAnswer {
  questionnaire: string;
  questionId: string;
  parentId?: string;
  answerId: string;
}
